import {
    Box,
    Typography,
    TextField,
    Button,
    LinearProgress,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import zxcvbn from 'zxcvbn';
import LogoTitle from './LogoTitle';
import TermsOfUse from '../components/TermsOfUse';
import { updateUserPasscode } from '../services/apiService';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import LoadingSpinner from './LoadingSpinner';

const PasswordReset = ({ userInfo, showLogin }) => {
    const dispatch = useDispatch();
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [strengthFeedback, setStrengthFeedback] = useState('');
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();

    const handlePasswordChange = (event) => {
        const newPassword = event.target.value;
        setPassword(newPassword);
        const strength = zxcvbn(newPassword);
        setStrengthFeedback(strength.feedback);
        setPasswordStrength(strength.score);
    };

    const handleRegistrationSuccess = () => {
        setTimeout(() => {
            navigate('/login');
        }, 2000);
    };

    const handleRegister = async (event) => {
        event.preventDefault();
        if (passwordStrength <= 2) {
            setError('Password is too weak.');
            return;
        }
        if (!agreeToTerms) {
            setError('You must agree to the terms of use.');
            return;
        }

        setError('');
        const formData = new FormData(event.target);
        const data = {
            password: formData.get('password'),
        };

        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 500));

        try {
            await updateUserPasscode(userInfo.userId, data.password, dispatch);
            setIsRegistered(true);
            localStorage.removeItem('tempToken');
            handleRegistrationSuccess();
        } catch (error) {
            console.error('Failed to register:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTermsOpen = () => setTermsOpen(true);
    const handleTermsClose = () => setTermsOpen(false);

    return (
        <Box>
            {isRegistered ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: '200px',
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: '8px',
                        padding: '20px',
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography variant="h6">Registration successful!</Typography>
                    <Typography variant="body2">Redirecting to login page...</Typography>
                    <LinearProgress sx={{ marginTop: 2 }} />
                </Box>
            ) : (
                <Box component="form" onSubmit={handleRegister}>
                    <Box
                        sx={{
                            padding: '20px',
                            height: '160px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: '8px',
                        }}
                    >
                        <Typography variant="h5">{`Hey, ${userInfo.firstname}!`}</Typography>
                        <Typography variant="h6">Thanks for testing.</Typography>
                    </Box>

                    <Box sx={{ marginTop: '20px' }}>
                        <Typography variant="body2">Please set a password.</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'row',
                                width: '100%',
                                alignContent: 'center',
                                justifyContent: 'center',

                            }}
                        >
                            <TextField
                                label="Password"
                                name="password"
                                type="password"
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                value={password}
                                onChange={handlePasswordChange}
                                disabled={loading}
                            />
                            <PasswordStrengthIndicator
                                passwordStrength={passwordStrength}
                                strengthFeedback={strengthFeedback}
                            />
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={agreeToTerms}
                                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                                    name="agreeToTerms"
                                    color="primary"
                                    disabled={loading}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    I agree to the{' '}
                                    <Button
                                        color="primary"
                                        onClick={handleTermsOpen}
                                        disabled={loading}
                                    >
                                        terms of use
                                    </Button>
                                </Typography>
                            }
                        />
                    </Box>

                    {loading ? (
                        <LoadingSpinner />
                    ) : (
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            Submit
                        </Button>
                    )}

                    {!loading && error && (
                        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            )}

            <Dialog open={termsOpen} onClose={handleTermsClose}>
                <DialogTitle>Terms of Use</DialogTitle>
                <DialogContent dividers sx={{ maxHeight: '500px', overflow: 'auto' }}>
                    <TermsOfUse />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleTermsClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PasswordReset;
